import { replayCascade } from "./cascade";
import type { ComponentInit } from "./registry";
import { settle, toMs } from "./settle";

const TABS = "[data-fa-tabs]";
const TAB = "[data-fa-tab]";

/** The widget's own selection rule, exported so a caller reads it rather than restating it. */
export const SELECTED_TAB = `${TAB}[aria-selected="true"]`;
const CASCADE = "[data-fa-cascade]";

const EXITING = "fa-tab-panel--exiting";
const EXIT_DURATION = "--fa-tab-exit";

export const FA_TAB_PAINT = "fa-tabs:paint";
export const FA_TAB_SELECT = "fa-tabs:select";

export type TabsPaintDetail = { panel: HTMLElement | null; selected: boolean };

export type TabsSelectDetail = { tab: HTMLElement; panel: HTMLElement | null };

declare global {
	interface HTMLElementEventMap {
		[FA_TAB_PAINT]: CustomEvent<TabsPaintDetail>;
		[FA_TAB_SELECT]: CustomEvent<TabsSelectDetail>;
	}
}

type TabState = {
	selected: string | null;
	tabindex: string | null;
	panelHidden: boolean | undefined;
};

const restoreAttribute = (element: HTMLElement, name: string, value: string | null) => {
	if (value === null) element.removeAttribute(name);
	else element.setAttribute(name, value);
};

/** Declared entirely in markup; see `Docs/Tabs`. */
export const initTabs: ComponentInit = (root) => {
	const items = [...root.querySelectorAll<HTMLElement>(TAB)].filter(
		(tab) => tab.closest(TABS) === root,
	);

	if (items.length === 0) return;

	const listeners = new AbortController();
	const options = { signal: listeners.signal };

	const panelFor = (tab: HTMLElement) => {
		const id = tab.getAttribute("aria-controls");

		return id === null ? null : root.ownerDocument.getElementById(id);
	};

	const initial: TabState[] = items.map((tab) => ({
		selected: tab.getAttribute("aria-selected"),
		tabindex: tab.getAttribute("tabindex"),
		panelHidden: panelFor(tab)?.hasAttribute("hidden"),
	}));

	const clamp = (index: number) => Math.min(Math.max(index, 0), items.length - 1);

	let current = clamp(
		Math.max(
			items.findIndex((tab) => tab.getAttribute("aria-selected") === "true"),
			0,
		),
	);
	let leaving: HTMLElement | null = null;
	let cancelExit: (() => void) | undefined;

	const settleExit = () => {
		cancelExit?.();
		cancelExit = undefined;

		if (!leaving) return;

		leaving.classList.remove(EXITING);
		leaving.setAttribute("hidden", "");
		leaving = null;
	};

	const paint = () => {
		items.forEach((tab, index) => {
			const selected = index === current;
			const panel = panelFor(tab);

			tab.setAttribute("aria-selected", String(selected));
			tab.setAttribute("tabindex", selected ? "0" : "-1");
			panel?.toggleAttribute("hidden", !selected);

			tab.dispatchEvent(
				new CustomEvent<TabsPaintDetail>(FA_TAB_PAINT, {
					bubbles: true,
					detail: { panel, selected },
				}),
			);
		});
	};

	const select = (index: number) => {
		const next = clamp(index);

		if (next === current) return;

		const outgoing = panelFor(items[current] as HTMLElement);

		current = next;

		settleExit();
		paint();

		const tab = items[current] as HTMLElement;
		const incoming = panelFor(tab);
		const groups = [
			...(incoming?.matches(CASCADE) ? [incoming] : []),
			...(incoming?.querySelectorAll<HTMLElement>(CASCADE) ?? []),
		];

		for (const group of groups) replayCascade(group);

		tab.dispatchEvent(
			new CustomEvent<TabsSelectDetail>(FA_TAB_SELECT, {
				bubbles: true,
				detail: { tab, panel: incoming },
			}),
		);

		if (!outgoing || outgoing === incoming) return;

		/* Read from the panel's own stylesheet, not mirrored: a number here and a duration there
		   drift apart silently, stranding the panel mid-fade or leaving it over the new one. */
		const exitMs = toMs(getComputedStyle(outgoing).getPropertyValue(EXIT_DURATION));

		if (exitMs <= 0) return;

		/* `paint` has just hidden it, and an exit plays on nothing while `hidden` holds. */
		outgoing.removeAttribute("hidden");
		outgoing.classList.add(EXITING);
		leaving = outgoing;
		cancelExit = settle(outgoing, exitMs, settleExit);
	};

	const indexOf = (target: EventTarget | null) =>
		target instanceof Element
			? items.indexOf(target.closest<HTMLElement>(TAB) as HTMLElement)
			: -1;

	const focusTab = (index: number) => {
		items[(index + items.length) % items.length]?.focus();
	};

	root.addEventListener(
		"click",
		(event) => {
			const index = indexOf(event.target);

			if (index >= 0) select(index);
		},
		options,
	);

	root.addEventListener(
		"keydown",
		(event) => {
			const index = indexOf(event.target);

			if (index < 0) return;

			const step = { ArrowLeft: -1, ArrowRight: 1 }[event.key];

			if (step !== undefined) focusTab(index + step);
			else if (event.key === "Home") focusTab(0);
			else if (event.key === "End") focusTab(items.length - 1);
			else return;

			event.preventDefault();
		},
		options,
	);

	paint();

	return () => {
		listeners.abort();
		settleExit();

		items.forEach((tab, index) => {
			const state = initial[index];

			if (!state) return;

			restoreAttribute(tab, "aria-selected", state.selected);
			restoreAttribute(tab, "tabindex", state.tabindex);

			const panel = panelFor(tab);

			if (panel && state.panelHidden !== undefined)
				panel.toggleAttribute("hidden", state.panelHidden);
		});
	};
};
